// Function index:
//   showAccountScreen(user) — mount the account/settings screen with usage and
//       billing sections, plus an unverified-email banner with a resend
//       button. Resolves when the user clicks "Back".

import { resendVerification, type AuthUser } from '../api.js';
import { renderUserUsage } from './account/usage.js';
import { renderBilling } from './account/billing.js';

export async function showAccountScreen(user: AuthUser): Promise<void> {
  const $ = (id: string): HTMLElement => document.getElementById(id)!;
  const screen = $('screen-account');
  const usageEl = $('account-usage');
  const billingEl = $('account-billing');
  const back = $('account-back') as HTMLButtonElement;

  screen.hidden = false;

  const ac = new AbortController();
  const on = (el: HTMLElement, ev: string, fn: (e: Event) => void): void =>
    el.addEventListener(ev, fn, { signal: ac.signal });

  // Unverified email: say so, and offer the resend the signup email promised.
  // The server answers honestly (503 when email is unconfigured), so the
  // message here can be trusted.
  let emailSection: HTMLDivElement | null = null;
  if (user.email_verified === false) {
    emailSection = document.createElement('div');
    emailSection.className = 'party-meta';
    emailSection.style.margin = '0 0 14px';
    const note = document.createElement('span');
    note.textContent = `${user.email} is not verified — password recovery needs a working address. `;
    const btn = document.createElement('button');
    btn.className = 'secondary';
    btn.textContent = 'Resend verification email';
    on(btn, 'click', async () => {
      btn.disabled = true;
      try {
        const res = await resendVerification();
        note.textContent = res.already_verified
          ? 'Already verified — you are all set. '
          : 'Sent — check your inbox (the link lasts 24 hours). ';
      } catch (err) {
        note.textContent = `${(err as Error).message} `;
        btn.disabled = false;
      }
    });
    emailSection.append(note, btn);
    usageEl.insertAdjacentElement('beforebegin', emailSection);
  }

  // Render the usage and billing sections
  await renderUserUsage(usageEl);
  await renderBilling(billingEl);

  return new Promise<void>((resolve) => {
    on(back, 'click', () => {
      ac.abort();
      // Injected per-open; remove so the next open doesn't stack a duplicate.
      emailSection?.remove();
      screen.hidden = true;
      resolve();
    });
  });
}
